/**
 * Age Determination Engine for Benefits Rating
 * 
 * Defines the DATE used to compute each covered person's "rating age" per coverage month,
 * which is then used to select the appropriate age-based rate (ACA single-age or band).
 * Composite-rated plans ignore this setting.
 */

// Rating Method determines whether age affects rates
export enum RatingMethod {
  ACA_AGE_CURVE = "aca_age_curve",       // Single-age rates (0-64, 64+)
  AGE_BANDED = "age_banded",             // Banded age tiers (e.g., 0-19, 20-24, etc.)
  AGE_BANDED_TIERED = "age_banded_tiered", // Age bands x coverage tiers matrix
  AGE_BASED_OTHER = "age_based_other",   // Future-proof enum
  COMPOSITE = "composite",               // Age does NOT affect rates
  TIERED_COMPOSITE = "tiered_composite", // Age does NOT affect rates (EE, ES, EC, EF)
  FAMILY_TIER_COMPOSITE = "family_tier_composite", // Age does NOT affect rates
}

// Age Determination Rules
export enum AgeDeterminationRule {
  PLAN_ANNIVERSARY = "plan_anniversary",                     // Rule 1
  PLAN_ANNIVERSARY_IGNORE_HIRE = "plan_anniversary_ignore_hire", // Rule 2
  FIRST_OF_MONTH = "first_of_month",                         // Rule 3
  END_OF_MONTH = "end_of_month",                             // Rule 4
  FIRST_OF_YEAR = "first_of_year",                           // Rule 5
}

// Age Determination Rule Labels for UI
export const AGE_DETERMINATION_OPTIONS = [
  { value: AgeDeterminationRule.PLAN_ANNIVERSARY, label: "Plan Anniversary" },
  { value: AgeDeterminationRule.PLAN_ANNIVERSARY_IGNORE_HIRE, label: "Plan Anniversary (Ignore Hire Date)" },
  { value: AgeDeterminationRule.FIRST_OF_MONTH, label: "First of the Month" },
  { value: AgeDeterminationRule.END_OF_MONTH, label: "End of the Month" },
  { value: AgeDeterminationRule.FIRST_OF_YEAR, label: "First of the Year" },
];

// Rating Method Labels for UI
export const RATING_METHOD_OPTIONS = [
  { value: RatingMethod.ACA_AGE_CURVE, label: "ACA Age Curve (Single-Age)", isAgeBased: true },
  { value: RatingMethod.AGE_BANDED, label: "Age Banded", isAgeBased: true },
  { value: RatingMethod.AGE_BANDED_TIERED, label: "Age Banded + Tiered", isAgeBased: true },
  { value: RatingMethod.AGE_BASED_OTHER, label: "Other Age-Based", isAgeBased: true },
  { value: RatingMethod.COMPOSITE, label: "Composite", isAgeBased: false },
  { value: RatingMethod.TIERED_COMPOSITE, label: "Tiered Composite (EE/ES/EC/EF)", isAgeBased: false },
  { value: RatingMethod.FAMILY_TIER_COMPOSITE, label: "Family Tier Composite", isAgeBased: false },
];

// Required data for age calculation
export interface PersonData {
  dob: Date | string;
  personCoverageStartDate?: Date | string;
}

export interface PlanData {
  planEffectiveDate: Date | string;
  ratingMethod?: RatingMethod | string;
  ageDetermination?: AgeDeterminationRule | string;
}

export interface CoverageMonth {
  start: Date; // First day of month (YYYY-MM-01)
  end: Date;   // Last day of month
}

export interface RatingAgeBand {
  ageBand: string;
  minAge: number;
  maxAge: number | null;
  rate?: number;
}

export interface AgeRate {
  age: number;
  rate: number;
}

export interface ValidationError {
  field: string;
  message: string;
  severity: 'error' | 'warning';
}

/**
 * Check if a rating method is age-dependent
 */
export function isAgeBasedRating(ratingMethod: RatingMethod | string | undefined | null): boolean {
  if (!ratingMethod) return false;
  const method = ratingMethod as RatingMethod;
  return [
    RatingMethod.ACA_AGE_CURVE,
    RatingMethod.AGE_BANDED,
    RatingMethod.AGE_BANDED_TIERED,
    RatingMethod.AGE_BASED_OTHER,
  ].includes(method);
}

/**
 * Parse a date string or Date object to Date
 */
function parseDate(dateInput: Date | string | undefined | null): Date | null {
  if (!dateInput) return null;
  if (dateInput instanceof Date) return dateInput;
  const parsed = new Date(dateInput);
  return isNaN(parsed.getTime()) ? null : parsed;
}

/**
 * Get the last day of a given month
 */
export function getLastDayOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0);
}

/**
 * Get the first day of a given month
 */
export function getFirstDayOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

/**
 * Create a CoverageMonth object from a year and month
 */
export function createCoverageMonth(year: number, month: number): CoverageMonth {
  const start = new Date(year, month - 1, 1);
  const end = getLastDayOfMonth(start);
  return { start, end };
}

/**
 * Standard Age Calculation Function (Shared across all modules)
 * 
 * Calculates age in years as of a given rating date.
 * Leap day (Feb 29) birthdays are treated as Feb 28 in non-leap years.
 * 
 * @param dob - Date of birth
 * @param ratingDate - The date to calculate age as of
 * @returns Age in whole years
 */
export function calculateAge(dob: Date | string, ratingDate: Date | string): number {
  const birthDate = parseDate(dob);
  const refDate = parseDate(ratingDate);
  
  if (!birthDate || !refDate) {
    throw new Error("Invalid date provided for age calculation");
  }

  let ageYears = refDate.getFullYear() - birthDate.getFullYear();

  // Leap day handling: Treat Feb 29 birthdays as Feb 28 in non-leap years
  let birthMonth = birthDate.getMonth();
  let birthDay = birthDate.getDate();
  
  // Check if birthday is Feb 29
  if (birthMonth === 1 && birthDay === 29) {
    // Check if ratingDate year is a leap year
    const isLeapYear = (year: number) => 
      (year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0);
    
    if (!isLeapYear(refDate.getFullYear())) {
      // Treat as Feb 28 in non-leap years
      birthDay = 28;
    }
  }

  // Check if birthday has occurred this year
  const refMonth = refDate.getMonth();
  const refDay = refDate.getDate();
  
  if (refMonth < birthMonth || (refMonth === birthMonth && refDay < birthDay)) {
    ageYears -= 1;
  }

  return Math.max(0, ageYears);
}

/**
 * Get the plan anniversary date for a given year
 */
function getPlanAnniversaryForYear(planEffectiveDate: Date, year: number): Date {
  return new Date(year, planEffectiveDate.getMonth(), planEffectiveDate.getDate());
}

/**
 * Get the plan year that contains a given coverage month
 */
function getPlanYearForCoverageMonth(coverageMonth: CoverageMonth, planEffectiveDate: Date): number {
  const covYear = coverageMonth.start.getFullYear();
  const annivThisYear = getPlanAnniversaryForYear(planEffectiveDate, covYear);
  
  // If coverage month is before this year's anniversary, we're in the previous plan year
  if (coverageMonth.start < annivThisYear) {
    return covYear - 1;
  }
  return covYear;
}

/**
 * Get Rating Date based on Age Determination Rule
 * 
 * @param rule - The age determination rule to apply
 * @param person - Person data including DOB and coverage start date
 * @param coverageMonth - The coverage month being calculated
 * @param plan - Plan data including effective date
 * @returns The rating date to use for age calculation
 */
export function getRatingDate(
  rule: AgeDeterminationRule | string,
  person: PersonData,
  coverageMonth: CoverageMonth,
  plan: PlanData
): Date {
  const planEffDate = parseDate(plan.planEffectiveDate);
  const personCovStart = parseDate(person.personCoverageStartDate);
  
  switch (rule) {
    case AgeDeterminationRule.PLAN_ANNIVERSARY: {
      // Rule 1: Plan Anniversary
      // Age is determined as of plan anniversary date for the plan year,
      // BUT a person who joins after the anniversary uses their own coverage start date
      // until the next anniversary.
      if (!planEffDate) {
        throw new Error("Plan effective date is required for Plan Anniversary rule");
      }
      
      const planYear = getPlanYearForCoverageMonth(coverageMonth, planEffDate);
      const anniversaryDate = getPlanAnniversaryForYear(planEffDate, planYear);
      
      // If person's coverage start is after this plan year's anniversary, use their start date
      if (personCovStart && personCovStart > anniversaryDate) {
        return personCovStart;
      }
      
      return anniversaryDate;
    }

    case AgeDeterminationRule.PLAN_ANNIVERSARY_IGNORE_HIRE: {
      // Rule 2: Plan Anniversary (Ignore Hire Date)
      // Everyone uses plan anniversary date, even mid-year joiners.
      if (!planEffDate) {
        throw new Error("Plan effective date is required for Plan Anniversary (Ignore Hire Date) rule");
      }
      
      const planYear = getPlanYearForCoverageMonth(coverageMonth, planEffDate);
      return getPlanAnniversaryForYear(planEffDate, planYear);
    }

    case AgeDeterminationRule.FIRST_OF_MONTH: {
      // Rule 3: First of the Month
      // Age as of the 1st day of each coverage month.
      return coverageMonth.start;
    }

    case AgeDeterminationRule.END_OF_MONTH: {
      // Rule 4: End of the Month
      // Age as of the last day of each coverage month.
      return coverageMonth.end;
    }

    case AgeDeterminationRule.FIRST_OF_YEAR: {
      // Rule 5: First of the Year
      // Age locked as of Jan 1 of the calendar year.
      return new Date(coverageMonth.start.getFullYear(), 0, 1);
    }

    default:
      // Default to first of month
      return coverageMonth.start;
  }
}

/**
 * Calculate the rating age for a person in a given coverage month
 * 
 * @param person - Person data
 * @param coverageMonth - Coverage month
 * @param plan - Plan data
 * @returns Rating age (integer)
 */
export function getRatingAge(
  person: PersonData,
  coverageMonth: CoverageMonth,
  plan: PlanData
): number {
  const rule = plan.ageDetermination || AgeDeterminationRule.FIRST_OF_MONTH;
  const ratingDate = getRatingDate(rule, person, coverageMonth, plan);
  return calculateAge(person.dob, ratingDate);
}

/**
 * Look up rate from ACA Age Curve
 * 
 * ACA age curve constraints:
 * - Ages 0-64 have individual rates
 * - Age 64+ uses the 64 rate (capped)
 * 
 * @param ratingAge - The calculated rating age
 * @param ageRates - Array of age rates from 0-64
 * @returns The monthly rate for the age
 */
export function lookupACAAgeCurveRate(
  ratingAge: number,
  ageRates: AgeRate[]
): number {
  if (ratingAge < 0) {
    throw new Error(`Invalid rating age: ${ratingAge}. Age cannot be negative.`);
  }

  // ACA caps at age 64
  const cappedAge = Math.min(ratingAge, 64);
  
  const ageRate = ageRates.find(r => r.age === cappedAge);
  
  if (!ageRate) {
    throw new Error(
      `Rating age ${cappedAge} does not have a configured rate in the ACA age curve. ` +
      `Ensure rates are defined for ages 0-64.`
    );
  }

  return ageRate.rate;
}

/**
 * Look up rate from Age Bands
 * 
 * @param ratingAge - The calculated rating age
 * @param ageBands - Array of age bands with minAge/maxAge
 * @returns The matching age band with rate
 */
export function lookupAgeBandRate(
  ratingAge: number,
  ageBands: RatingAgeBand[]
): RatingAgeBand {
  if (ratingAge < 0) {
    throw new Error(`Invalid rating age: ${ratingAge}. Age cannot be negative.`);
  }

  const band = ageBands.find(b => {
    const minMatch = ratingAge >= b.minAge;
    const maxMatch = b.maxAge === null || ratingAge <= b.maxAge;
    return minMatch && maxMatch;
  });

  if (!band) {
    throw new Error(
      `Rating age ${ratingAge} does not map to any configured age band. ` +
      `Check age band configuration for gaps.`
    );
  }

  return band;
}

/**
 * Parse an age band label to extract min/max ages
 * Examples: "25" -> {min: 25, max: 25}, "30-34" -> {min: 30, max: 34}, "64+" -> {min: 64, max: null}
 */
export function parseAgeBandLabel(label: string): { minAge: number; maxAge: number | null } {
  const normalized = label.trim();
  
  // Handle "64+" or "65+" format
  if (normalized.endsWith('+')) {
    const min = parseInt(normalized.replace('+', ''), 10);
    return { minAge: min, maxAge: null };
  }
  
  // Handle "0-14" or "30-34" format
  if (normalized.includes('-')) {
    const [minStr, maxStr] = normalized.split('-').map(s => s.trim());
    const min = parseInt(minStr, 10);
    const max = parseInt(maxStr, 10);
    return { minAge: min, maxAge: max };
  }
  
  // Handle single age "25" format
  const age = parseInt(normalized, 10);
  return { minAge: age, maxAge: age };
}

/**
 * Validate age determination configuration
 * 
 * @param plan - Plan data to validate
 * @param persons - Array of persons to validate
 * @param ageBands - Age bands to validate (for age-banded rating)
 * @param ageRates - Age rates to validate (for ACA age curve)
 * @returns Array of validation errors/warnings
 */
export function validateAgeDeterminationConfig(
  plan: PlanData,
  persons: PersonData[],
  ageBands?: RatingAgeBand[],
  ageRates?: AgeRate[]
): ValidationError[] {
  const errors: ValidationError[] = [];
  const ratingMethod = plan.ratingMethod as RatingMethod;
  
  // Skip validation for non-age-based rating
  if (!isAgeBasedRating(ratingMethod)) {
    return errors;
  }

  // Check for missing DOBs
  persons.forEach((person, index) => {
    if (!person.dob) {
      errors.push({
        field: `persons[${index}].dob`,
        message: `Person ${index + 1} is missing date of birth. DOB is required for age-based rating.`,
        severity: 'error',
      });
    }
  });

  // Check for missing plan effective date when using Plan Anniversary rules
  const ageDetermination = plan.ageDetermination as AgeDeterminationRule;
  if (
    (ageDetermination === AgeDeterminationRule.PLAN_ANNIVERSARY ||
     ageDetermination === AgeDeterminationRule.PLAN_ANNIVERSARY_IGNORE_HIRE) &&
    !plan.planEffectiveDate
  ) {
    errors.push({
      field: 'plan.planEffectiveDate',
      message: 'Plan effective date is required for Plan Anniversary age determination rules.',
      severity: 'error',
    });
  }

  // Check for missing coverage start dates when using Plan Anniversary rule
  if (ageDetermination === AgeDeterminationRule.PLAN_ANNIVERSARY) {
    persons.forEach((person, index) => {
      if (!person.personCoverageStartDate) {
        errors.push({
          field: `persons[${index}].personCoverageStartDate`,
          message: `Person ${index + 1} is missing coverage start date. Required for Plan Anniversary rule.`,
          severity: 'error',
        });
      }
    });
  }

  // Warning for Plan Anniversary Ignore Hire when person joined after anniversary
  if (ageDetermination === AgeDeterminationRule.PLAN_ANNIVERSARY_IGNORE_HIRE) {
    const planEffDate = parseDate(plan.planEffectiveDate);
    if (planEffDate) {
      persons.forEach((person, index) => {
        const covStart = parseDate(person.personCoverageStartDate);
        if (covStart) {
          const annivThisYear = getPlanAnniversaryForYear(planEffDate, covStart.getFullYear());
          if (covStart > annivThisYear) {
            errors.push({
              field: `persons[${index}].personCoverageStartDate`,
              message: `Person ${index + 1} joined after plan anniversary. Their rating age may differ from other carriers.`,
              severity: 'warning',
            });
          }
        }
      });
    }
  }

  // Validate ACA age curve completeness
  if (ratingMethod === RatingMethod.ACA_AGE_CURVE && ageRates) {
    const ages = new Set(ageRates.map(r => r.age));
    for (let age = 0; age <= 64; age++) {
      if (!ages.has(age)) {
        errors.push({
          field: 'ageRates',
          message: `ACA age curve is missing rate for age ${age}. All ages 0-64 must have rates.`,
          severity: 'error',
        });
      }
    }
  }

  // Validate age bands for gaps and overlaps
  if (ratingMethod === RatingMethod.AGE_BANDED && ageBands && ageBands.length > 0) {
    // Sort by minAge
    const sorted = [...ageBands].sort((a, b) => a.minAge - b.minAge);
    
    let expectedMin = 0;
    sorted.forEach((band, index) => {
      // Check for gaps
      if (band.minAge > expectedMin) {
        errors.push({
          field: 'ageBands',
          message: `Age band gap detected: ages ${expectedMin}-${band.minAge - 1} have no band.`,
          severity: 'error',
        });
      }
      
      // Check for overlaps with previous band
      if (index > 0) {
        const prevBand = sorted[index - 1];
        if (prevBand.maxAge !== null && band.minAge <= prevBand.maxAge) {
          errors.push({
            field: 'ageBands',
            message: `Age band overlap detected: "${prevBand.ageBand}" and "${band.ageBand}" overlap.`,
            severity: 'error',
          });
        }
      }
      
      expectedMin = (band.maxAge !== null ? band.maxAge : band.minAge) + 1;
    });
  }

  return errors;
}

/**
 * Map rate structure type to rating method
 */
export function rateStructureToRatingMethod(rateStructureType: string | undefined | null): RatingMethod {
  switch (rateStructureType) {
    case 'age_rates':
    case 'wb_table':
      return RatingMethod.ACA_AGE_CURVE;
    case 'age_tier_rates':
      return RatingMethod.AGE_BANDED_TIERED;
    case 'tier_rates':
      return RatingMethod.TIERED_COMPOSITE;
    case 'table_rates':
      return RatingMethod.COMPOSITE;
    default:
      return RatingMethod.TIERED_COMPOSITE;
  }
}

/**
 * Determine if Age Determination dropdown should be shown/enabled
 * based on the rate structure type
 */
export function shouldShowAgeDetermination(rateStructureType: string | undefined | null): boolean {
  if (!rateStructureType) return false;
  return ['age_rates', 'wb_table', 'age_tier_rates'].includes(rateStructureType);
}

/**
 * Determine the date used for age-based rate lookups.
 * Single source of truth — imported by both server and client.
 */
export function getAgeAsOfDate(
  plan: { effectiveDate?: string | Date | null; ageDetermination?: string | null },
  employee: { dateOfBirth?: string | Date | null; hireDate?: string | Date | null },
  enrollmentEffectiveDate?: Date | string,
): Date | undefined {
  const ageDetermination = (plan?.ageDetermination as AgeDeterminationRule) || AgeDeterminationRule.PLAN_ANNIVERSARY;
  const planEffective = plan?.effectiveDate ? new Date(plan.effectiveDate) : null;
  const coverageStart = enrollmentEffectiveDate
    ? new Date(enrollmentEffectiveDate)
    : (employee?.hireDate ? new Date(employee.hireDate) : null);

  if (!employee?.dateOfBirth) return undefined;

  if (planEffective && coverageStart) {
    try {
      const coverageMonth = createCoverageMonth(coverageStart.getFullYear(), coverageStart.getMonth() + 1);
      return getRatingDate(
        ageDetermination,
        { dob: employee.dateOfBirth, personCoverageStartDate: coverageStart },
        coverageMonth,
        { planEffectiveDate: planEffective, ageDetermination },
      );
    } catch {
      // Fallback below
    }
  }

  if (coverageStart) return coverageStart;
  if (planEffective) return planEffective;
  return undefined;
}

/**
 * Resolve the enrollment effective date against the plan's own coverage window.
 *
 * Age-rated premiums measure the member's age as of the effective date's plan
 * year. Letting the effective date fall in a prior year (e.g. a long-tenured
 * employee's old hire date, or a blank date defaulting to "today" during open
 * enrollment) rates the member years too young.
 *
 * Rules:
 *  - blank date  → defaults to the plan's start date (a default, not a correction)
 *  - before the plan's start → `tooEarly`
 *  - after the plan year ends → `tooLate`
 *
 * Both flags let the caller REJECT the date and show the allowed range. We never
 * silently rewrite an explicit date the user typed.
 *
 * The plan year ends at `endDate` when set (inclusive), otherwise the day before
 * `renewalDate` — renewalDate is the NEXT year's start, so it is exclusive.
 *
 * Works in calendar-date (YYYY-MM-DD) space to stay timezone-safe.
 */
export function resolveEnrollmentEffectiveDate(
  raw: Date | string | null | undefined,
  plan:
    | { effectiveDate?: Date | string | null; renewalDate?: Date | string | null; endDate?: Date | string | null }
    | null
    | undefined,
): {
  effectiveDate?: string;
  planStart?: string;
  planEnd?: string;
  tooEarly: boolean;
  tooLate: boolean;
} {
  const toYmd = (value: Date | string | null | undefined): string | undefined => {
    if (!value) return undefined;
    if (value instanceof Date) {
      // Date inputs here encode a calendar date at UTC midnight (`new Date("YYYY-MM-DD")`
      // and Postgres timestamps), so read them in UTC to avoid an off-by-one.
      const year = value.getUTCFullYear();
      const month = String(value.getUTCMonth() + 1).padStart(2, "0");
      const day = String(value.getUTCDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    }
    return value.match(/^\d{4}-\d{2}-\d{2}/)?.[0];
  };

  // renewalDate is the next plan year's start, so the last covered day is the day before.
  const dayBefore = (ymd: string): string => {
    const d = new Date(`${ymd}T00:00:00.000Z`);
    d.setUTCDate(d.getUTCDate() - 1);
    return d.toISOString().slice(0, 10);
  };

  const planStart = toYmd(plan?.effectiveDate);
  const renewal = toYmd(plan?.renewalDate);
  const planEnd = toYmd(plan?.endDate) ?? (renewal ? dayBefore(renewal) : undefined);
  const given = toYmd(raw);

  // Blank → default to the plan's start.
  if (!given) return { effectiveDate: planStart, planStart, planEnd, tooEarly: false, tooLate: false };

  // YYYY-MM-DD strings compare lexicographically == chronologically.
  // Coverage can't begin before the plan year starts or after it ends —
  // report it, don't rewrite it.
  const tooEarly = !!planStart && given < planStart;
  const tooLate = !tooEarly && !!planEnd && given > planEnd;

  return { effectiveDate: given, planStart, planEnd, tooEarly, tooLate };
}
